import { LexicalEditor } from "@/components";
import { addProductSchema } from "@/schemas/AddProductSchema";
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from "@/components/ui/accordion";
import { zodResolver } from "@hookform/resolvers/zod";
import React, { useEffect, useRef } from "react";
import { useFieldArray, useForm } from "react-hook-form";
import { z } from "zod";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "../ui/form";
import { Input } from "../ui/input";
import { Button } from "../ui/button";
import { useState } from "react";
import { VariantAttribute } from "@/types/AppTypes";
import { useAppDispatch, useAppSelector } from "@/store/hooks";

import { v4 as uuidv4 } from "uuid";
import { ChevronsUpDown, Delete, LoaderCircle, Plus, Upload, X } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";

import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "../ui/command";
import { Checkbox } from "../ui/checkbox";
import { generateClient } from "@/utils/helper/aws-generateClient-singleton";
import { executeGraphQLMutation } from "@/utils/helper/graphql-helper";
import { createProductInfo, createVariantAttribute, updateProductInfo } from "@/graphql/mutations";
import ls from "@/utils/SecureLS";
import { uploadFileToBucket } from "@/utils/helper/aws-bucketUpload";

import { toast } from "sonner";

import { RadioGroup, RadioGroupItem } from "../ui/radio-group";
import { ScrollArea } from "../ui/scroll-area";
import { Card, CardContent } from "../ui/card";
import { Switch } from "../ui/switch";
import { addProduct } from "@/store/slices/sellerDashboardSlice";
import capitalizeWords from "@/utils/helper/capitalizeWords";

type ProductFormValues = z.infer<typeof addProductSchema>;

const AddProductForm = React.memo(
  ({
    product,
    attributes,
    isEditing = false,
  }: {
    product?: any;
    attributes: VariantAttribute[];
    isEditing?: boolean;
  }) => {
    const [attributesData, setAttributesData] = useState<VariantAttribute[]>([]);
    const defaultVariantRef = useRef<number | null>(null);
    const { brands, categories } = useAppSelector((state) => state.app);
    const [brandsTimeoutElapsed, setBrandsTimeoutElapsed] = useState<boolean>(false);
    const [categoriesTimeoutElapsed, setCategoriesTimeoutElapsed] = useState<boolean>(false);
    const [previewUrlsMap, setPreviewUrlsMap] = useState<Record<number, string[]>>({});
    const [searchValue, setSearchValue] = useState<string>("");
    const [attributePopoverOpenMap, setAttributePopoverOpenMap] = useState<Record<number, boolean>>(
      {}
    );

    const [publishLoading, setPublishLoading] = useState<boolean>(false);
    const dispatch = useAppDispatch();

    const userLs = ls.get("user") || "";
    const user = JSON.parse(userLs);

    const client = generateClient();
    const form = useForm<ProductFormValues>({
      resolver: zodResolver(addProductSchema),
      defaultValues: {
        brand: product?.brand_id || "",
        categories: product?.category_id || [],
        name: capitalizeWords(product?.name || ""),
        variants:
          isEditing && product?.variants && product.variants.length > 0
            ? product.variants.map((variant: any) => {
                // Parse variant if it's a string
                const varData = typeof variant === "string" ? JSON.parse(variant) : variant;

                return {
                  id: varData?.id,
                  description: varData?.description || "",
                  price: varData?.price || 0.0,
                  images: varData?.images || [],
                  sku: varData?.sku || "",
                  on_hand: varData?.on_hand || 0,
                  show_by_default: varData?.show_by_default || false,
                  attributes: varData?.attributes || {},
                };
              })
            : [
                {
                  description: "",
                  price: 0.0,
                  images: [],
                  sku: "",
                  on_hand: 0,
                  show_by_default: false,
                  attributes: {},
                },
              ],
      },
    });

    const fileInputRefs = useRef<Record<number, HTMLInputElement | null>>({});

    const { fields, append, remove } = useFieldArray({
      control: form.control,
      name: "variants",
    });

    const handleImageUpload = async (files: FileList | null, variantIndex: number) => {
      if (!files) return;

      const currentFormImages = form.getValues(`variants.${variantIndex}.images`) || [];
      const newPreviewUrls = [...(previewUrlsMap[variantIndex] || [])];
      const updatedImages = [...currentFormImages];

      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const previewUrl = URL.createObjectURL(file);
        newPreviewUrls.push(previewUrl);

        updatedImages.push({
          file: file,
          isNew: true,
          name: file.name,
        });
      }

      form.setValue(`variants.${variantIndex}.images`, updatedImages);

      setPreviewUrlsMap((prev) => ({
        ...prev,
        [variantIndex]: newPreviewUrls,
      }));
    };
    const handleRemoveImage = (indexToRemove: number, variantIndex: number) => {
      const variantPreviewUrls = previewUrlsMap[variantIndex] || [];
      const urlToRevoke = variantPreviewUrls[indexToRemove];

      const updatedPreviewUrls = variantPreviewUrls.filter((_, idx) => idx !== indexToRemove);
      const updatedFormImages = (form.getValues(`variants.${variantIndex}.images`) || []).filter(
        (_: any, idx: any) => idx !== indexToRemove
      );

      URL.revokeObjectURL(urlToRevoke);

      setPreviewUrlsMap((prev) => ({
        ...prev,
        [variantIndex]: updatedPreviewUrls,
      }));

      form.setValue(`variants.${variantIndex}.images`, updatedFormImages);
      const currentRef = fileInputRefs?.current;
      if (updatedPreviewUrls.length === 0 && currentRef && currentRef[variantIndex]) {
        currentRef[variantIndex]!.value = "";
      }
    };

    const handleAddNewAttribute = async () => {
      if (!searchValue.trim()) return;

      try {
        const newAttribute = {
          id: uuidv4(),
          name: searchValue.toLowerCase().trim(),
          created_by: user?.user_id,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        };
        setAttributesData((prev) => [...prev, newAttribute]);
        await executeGraphQLMutation({
          query: createVariantAttribute,
          variables: {
            input: newAttribute,
          },
          client,
        });

        setSearchValue("");

        return newAttribute;
      } catch (error) {
        console.error("Error creating new attribute:", error);
        return null;
      }
    };

    const onSubmit = async (data: ProductFormValues) => {
      if (defaultVariantRef.current === null) {
        data.variants[0].show_by_default = true;
        form.setValue(`variants.0.show_by_default`, true);
        defaultVariantRef.current = 0;
      }

      setPublishLoading(true);

      const processVariantImages = async (variants: any[]) => {
        return Promise.all(
          variants.map(async (variant) => {
            const processedImages = await Promise.all(
              variant.images.map(async (image: any) => {
                if (image.isNew && image.file) {
                  const uploadedUrl = await uploadFileToBucket(image.file);
                  return uploadedUrl;
                }

                return typeof image === "string" ? image : image.url || "";
              })
            );

            return {
              ...variant,
              images: processedImages,
            };
          })
        );
      };

      const processedVariants = await processVariantImages(data.variants);

      const productInfo = {
        id: product?.id || uuidv4(),
        brand_id: data?.brand,
        name: data?.name?.toLowerCase() || "",
        is_active: true,
        is_deleted: false,
        updated_at: new Date().toISOString(),
        ...(isEditing ? {} : { created_at: new Date().toISOString() }),
        seller_id: user?.user_id,
        category_id: data.categories,
        variants: processedVariants.map((variant) => {
          const variantData = {
            ...variant,
            id: variant.id || uuidv4(),
            is_active: true,
          };
          return JSON.stringify(variantData);
        }),
      };

      try {
        const query = isEditing ? updateProductInfo : createProductInfo;
        const result = await executeGraphQLMutation({
          query: query,
          variables: {
            input: productInfo,
          },
          client,
        });

        if (result) {
          dispatch(addProduct(result));

          if (!isEditing) {
            form.reset();
            setPreviewUrlsMap({});
            defaultVariantRef.current = null;
          }
          toast.success(`Product ${isEditing ? "updated" : "created"} successfully!`);
        }
      } catch (error) {
        console.error(`Error ${isEditing ? "updating" : "creating"} product:`, error);
        toast.error(`Failed to ${isEditing ? "update" : "create"} product.`);
      } finally {
        setPublishLoading(false);
      }
    };

    useEffect(() => {
      if (attributes?.length) {
        setAttributesData(attributes);
      }
    }, [attributes]);

    useEffect(() => {
      let brandsTimeoutId: NodeJS.Timeout | null = null;
      let categoriesTimeoutId: NodeJS.Timeout | null = null;

      if (!brands || brands.length === 0) {
        brandsTimeoutId = setTimeout(() => {
          setBrandsTimeoutElapsed(true);
        }, 20000);
      }

      if (!categories || categories.length === 0) {
        categoriesTimeoutId = setTimeout(() => {
          setCategoriesTimeoutElapsed(true);
        }, 20000);
      }

      return () => {
        if (brandsTimeoutId) clearTimeout(brandsTimeoutId);
        if (categoriesTimeoutId) clearTimeout(categoriesTimeoutId);
      };
    }, [brands, categories]);

    const [userClosedAll, setUserClosedAll] = useState<boolean>(false);

    const [openAccordion, setOpenAccordion] = useState<string>(fields[0]?.id || "");

    useEffect(() => {
      if (
        fields.length > 0 &&
        !fields.some((field) => field.id === openAccordion) &&
        !userClosedAll
      ) {
        setOpenAccordion(fields[0].id);
      }
    }, [fields, openAccordion, userClosedAll]);

    useEffect(() => {
      if (isEditing && product?.variants) {
        const initialPreviewUrls: Record<number, string[]> = {};

        product.variants.forEach((variant: any, index: number) => {
          const varData = typeof variant === "string" ? JSON.parse(variant) : variant;
          if (varData.images && Array.isArray(varData.images)) {
            initialPreviewUrls[index] = varData.images;
          }
        });

        setPreviewUrlsMap(initialPreviewUrls);

        product.variants.forEach((variant: any, index: number) => {
          const varData = typeof variant === "string" ? JSON.parse(variant) : variant;
          if (varData.show_by_default) {
            defaultVariantRef.current = index;
          }
        });
      }
    }, [isEditing, product]);

    return (
      <div className="pb-8 mx-auto w-full">
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit, (errors) => {
              if (errors.variants && Array.isArray(errors.variants)) {
                const firstErrorIndex = errors.variants.findIndex(
                  (v) => v && Object.keys(v).length > 0
                );
                if (firstErrorIndex !== -1) {
                  setOpenAccordion(fields[firstErrorIndex].id);
                  document
                    .getElementById(`variant-accordion-${fields[firstErrorIndex].id}`)
                    ?.scrollIntoView({ behavior: "smooth" });

                  toast.error(`Variant #${firstErrorIndex + 1} has missing or invalid fields.`, {
                    description: "Please review and correct them.",
                    duration: 5000,
                    position: "top-right",
                  });
                }
              }
            })}
          >
            <div className="grid grid-cols-[1fr_370px] gap-4 ">
              <div>
                <FormField
                  control={form.control}
                  name={"name"}
                  render={({ field }) => (
                    <FormItem className="pt-2 pb-6">
                      <FormLabel>Name *</FormLabel>
                      <FormControl>
                        <Input placeholder="Name" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <Accordion
                  type="single"
                  collapsible={true}
                  value={openAccordion}
                  onValueChange={(value) => {
                    if (value === undefined || value === "") {
                      setUserClosedAll(true);
                    } else {
                      setUserClosedAll(false);
                    }
                    setOpenAccordion(value);
                  }}
                  className="transition-all"
                >
                  {fields.map((variant, index) => {
                    const attributesValues = form.watch(`variants.${index}.attributes`);

                    return (
                      <Card className="px-1 mb-4 py-0  ">
                        <CardContent className="py-0 px-2">
                          <AccordionItem key={variant.id} value={variant.id}>
                            <AccordionTrigger
                              className={`px-3   transition duration-300 border-none text-lg font-medium`}
                            >
                              {`Variant ${index + 1}`}
                            </AccordionTrigger>
                            <AccordionContent className="px-3 transition-all duration-500 ">
                              <div className="space-y-6 mt-2">
                                <FormField
                                  control={form.control}
                                  name={`variants.${index}.description`}
                                  render={({ field }) => (
                                    <FormItem>
                                      <FormLabel>Description</FormLabel>
                                      <FormControl>
                                        <LexicalEditor
                                          value={typeof field.value === "string" ? field.value : ""}
                                          onChange={field.onChange}
                                          placeholder="Description..."
                                          disabled={false}
                                          onKeyDown={(e) => {
                                            if (e.key === "Enter" && !e.shiftKey) {
                                              e.preventDefault();
                                            }
                                          }}
                                        />
                                      </FormControl>
                                      <FormMessage />
                                    </FormItem>
                                  )}
                                />
                                <div className="grid grid-cols-2 gap-2">
                                  <FormField
                                    control={form.control}
                                    name={`variants.${index}.price`}
                                    render={({ field }) => (
                                      <FormItem>
                                        <FormLabel>Price *</FormLabel>
                                        <FormControl>
                                          <Input
                                            type="number"
                                            step="0.01"
                                            {...field}
                                            onChange={(e) => field.onChange(Number(e.target.value))}
                                            value={field.value}
                                          />
                                        </FormControl>
                                        <FormMessage />
                                      </FormItem>
                                    )}
                                  />

                                  <FormField
                                    control={form.control}
                                    name={`variants.${index}.sku`}
                                    render={({ field }) => (
                                      <FormItem>
                                        <FormLabel>SKU *</FormLabel>
                                        <FormControl>
                                          <Input placeholder="SKU" {...field} />
                                        </FormControl>
                                        <FormMessage />
                                      </FormItem>
                                    )}
                                  />
                                </div>
                                <div>
                                  <FormField
                                    control={form.control}
                                    name={`variants.${index}.images`}
                                    render={({ field }) => (
                                      <FormItem>
                                        <FormLabel>Images</FormLabel>
                                        <FormControl>
                                          <Input
                                            type="file"
                                            multiple
                                            accept="image/jpeg,image/jpg,image/png"
                                            ref={(el) => {
                                              if (!fileInputRefs.current) {
                                                fileInputRefs.current = {};
                                              }
                                              fileInputRefs.current[index] = el;
                                            }}
                                            onChange={(e) =>
                                              handleImageUpload(e.target.files, index)
                                            }
                                            className="hidden"
                                          />
                                        </FormControl>
                                        <FormMessage />
                                      </FormItem>
                                    )}
                                  />
                                  <div
                                    className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-gray-400 transition-colors cursor-pointer mt-3"
                                    onClick={() => fileInputRefs.current?.[index]?.click()}
                                  >
                                    <Upload className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                                    <div className="text-sm text-gray-600">
                                      <span className="font-medium text-blue-600">
                                        Choose Files
                                      </span>
                                      <span className="ml-2">
                                        {(previewUrlsMap[index] || [])?.length > 0
                                          ? `${
                                              (previewUrlsMap[index] || []).length
                                            } file(s) selected`
                                          : "No files selected"}
                                      </span>
                                    </div>
                                    <div className="text-xs text-gray-500 mt-1">
                                      Supported file types: .jpeg, .jpg, .png
                                    </div>
                                  </div>
                                </div>
                                {(previewUrlsMap[index] || [])?.length > 0 && (
                                  <div className="mt-2 grid grid-cols-2 sm:grid-cols-2 md:grid-cols-5 gap-2">
                                    {(previewUrlsMap[index] || [])?.map((url, previewIndex) => (
                                      <div key={previewIndex} className="relative inline-block">
                                        <img
                                          src={url}
                                          alt={`Preview ${previewIndex + 1}`}
                                          className="max-h-60 rounded-md border object-cover"
                                        />
                                        <X
                                          onClick={() => handleRemoveImage(previewIndex, index)}
                                          className="absolute -top-2 -right-2 z-10 w-5 h-5 cursor-pointer bg-white  rounded-full p-1 text-black  border border-gray-300"
                                        />
                                      </div>
                                    ))}
                                  </div>
                                )}

                                {(previewUrlsMap[index] || [])?.length === 0 &&
                                  form?.watch(`variants.${index}.images`)?.[0] && (
                                    <div className="mt-2">
                                      <p className="text-sm text-gray-500">
                                        Files selected but preview not available.
                                      </p>
                                    </div>
                                  )}

                                <FormField
                                  control={form.control}
                                  name={`variants.${index}.on_hand`}
                                  render={({ field }) => (
                                    <FormItem>
                                      <FormLabel>Stock</FormLabel>
                                      <FormControl>
                                        <Input
                                          type="number"
                                          {...field}
                                          onChange={(e) => field.onChange(Number(e.target.value))}
                                          value={field.value}
                                        />
                                      </FormControl>
                                      <FormMessage />
                                    </FormItem>
                                  )}
                                />

                                <FormField
                                  control={form.control}
                                  name={`variants.${index}.show_by_default`}
                                  render={({ field }) => (
                                    <FormItem className="flex items-center space-x-2">
                                      <FormControl>
                                        <div className="flex flex-row items-center space-x-2">
                                          <Switch
                                            checked={field.value}
                                            onCheckedChange={(checked) => {
                                              if (checked === true) {
                                                if (
                                                  defaultVariantRef.current !== null &&
                                                  defaultVariantRef.current !== index
                                                ) {
                                                  form.setValue(
                                                    `variants.${defaultVariantRef.current}.show_by_default`,
                                                    false
                                                  );
                                                }

                                                defaultVariantRef.current = index;
                                              } else if (
                                                checked === false &&
                                                defaultVariantRef.current === index
                                              ) {
                                                defaultVariantRef.current = null;
                                              }

                                              field.onChange(checked);
                                            }}
                                            disabled={publishLoading}
                                          />
                                        </div>
                                      </FormControl>
                                      <FormLabel>Show by Default</FormLabel>
                                      <FormMessage />
                                    </FormItem>
                                  )}
                                />

                                <div className="mt-4 space-y-4 ">
                                  <label className="block font-medium">Attributes</label>

                                  {Object.entries(attributesValues || {}).map(
                                    ([attrName, attrValue], attrIndex) =>
                                      attrName && (
                                        <div
                                          key={attrIndex}
                                          className="grid grid-cols-[1fr,1fr,auto] gap-2 items-end w-full "
                                        >
                                          <div className="flex flex-col gap-2 ">
                                            <FormLabel>Attribute Name</FormLabel>
                                            <Input value={attrName} disabled className="bg-muted" />
                                          </div>
                                          <div className="flex flex-col gap-2 ">
                                            <FormLabel>Value</FormLabel>
                                            <Input
                                              placeholder="e.g. Red"
                                              value={attrValue as string}
                                              onChange={(e) => {
                                                const currentAttributes = {
                                                  ...(form.getValues(
                                                    `variants.${index}.attributes`
                                                  ) || {}),
                                                };
                                                currentAttributes[attrName] = e.target.value;
                                                form.setValue(
                                                  `variants.${index}.attributes`,
                                                  currentAttributes,
                                                  {
                                                    shouldDirty: true,
                                                    shouldTouch: true,
                                                    shouldValidate: true,
                                                  }
                                                );
                                              }}
                                            />
                                          </div>
                                          <Button
                                            className="p-0 m-0 h-10"
                                            type="button"
                                            variant="outline"
                                            onClick={() => {
                                              const currentAttributes = {
                                                ...(form.getValues(
                                                  `variants.${index}.attributes`
                                                ) || {}),
                                              };

                                              delete currentAttributes[attrName];

                                              form.setValue(
                                                `variants.${index}.attributes`,
                                                currentAttributes,
                                                {
                                                  shouldDirty: true,
                                                  shouldTouch: true,
                                                  shouldValidate: true,
                                                }
                                              );
                                            }}
                                          >
                                            <Delete className="h-5 w-5 text-red-500" />
                                          </Button>
                                        </div>
                                      )
                                  )}

                                  <div className="grid grid-cols-1 gap-2 items-end mt-4 ">
                                    <div className="w-full">
                                      <Popover
                                        open={attributePopoverOpenMap[index] || false}
                                        onOpenChange={(open) => {
                                          setAttributePopoverOpenMap((prev) => ({
                                            ...prev,
                                            [index]: open,
                                          }));
                                        }}
                                      >
                                        <PopoverTrigger className=" w-full relative h-10" asChild>
                                          <Button
                                            variant="outline"
                                            role="combobox"
                                            className="w-full justify-between"
                                            onClick={() => {
                                              setAttributePopoverOpenMap((prev) => ({
                                                ...prev,
                                                [index]: true,
                                              }));
                                            }}
                                          >
                                            Select Attribute
                                            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                                          </Button>
                                        </PopoverTrigger>
                                        <PopoverContent className=" w-[var(--radix-popper-anchor-width)] p-0 ">
                                          <Command>
                                            <CommandInput
                                              className=" focus:outline-none"
                                              placeholder="Search attributes..."
                                              value={searchValue}
                                              onValueChange={setSearchValue}
                                            />
                                            <CommandList>
                                              <CommandEmpty>
                                                No attribute found.
                                                <Button
                                                  type="button"
                                                  variant="outline"
                                                  className="w-full mt-2"
                                                  onClick={async () => {
                                                    const newAttribute =
                                                      await handleAddNewAttribute();
                                                    if (newAttribute) {
                                                      const currentAttributes =
                                                        form.getValues(
                                                          `variants.${index}.attributes`
                                                        ) || {};
                                                      form.setValue(
                                                        `variants.${index}.attributes`,
                                                        {
                                                          ...currentAttributes,
                                                          [newAttribute.name]: "",
                                                        }
                                                      );
                                                      setAttributePopoverOpenMap((prev) => ({
                                                        ...prev,
                                                        [index]: false,
                                                      }));
                                                    }
                                                  }}
                                                >
                                                  Add "{searchValue}" as new attribute
                                                </Button>
                                              </CommandEmpty>
                                              <CommandGroup>
                                                {attributesData.map((attribute) => (
                                                  <CommandItem
                                                    value={attribute?.name ?? ""}
                                                    key={attribute?.id}
                                                    onSelect={() => {
                                                      const currentAttributes =
                                                        form.getValues(
                                                          `variants.${index}.attributes`
                                                        ) || {};

                                                      if (
                                                        !currentAttributes[attribute?.name ?? ""]
                                                      ) {
                                                        form.setValue(
                                                          `variants.${index}.attributes`,
                                                          {
                                                            ...currentAttributes,
                                                            [attribute?.name ?? ""]: "",
                                                          }
                                                        );
                                                      }

                                                      setAttributePopoverOpenMap((prev) => ({
                                                        ...prev,
                                                        [index]: false,
                                                      }));

                                                      setSearchValue("");
                                                    }}
                                                  >
                                                    {attribute.name}
                                                  </CommandItem>
                                                ))}
                                              </CommandGroup>
                                            </CommandList>
                                          </Command>
                                        </PopoverContent>
                                      </Popover>
                                    </div>
                                  </div>
                                  {index > 0 && (
                                    <div className="flex items-center justify-end">
                                      <Button
                                        type="button"
                                        className="bg-red-500 text-right text-white hover:bg-red-600"
                                        onClick={() => remove(index)}
                                      >
                                        Remove Variant
                                      </Button>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </AccordionContent>
                          </AccordionItem>
                        </CardContent>
                      </Card>
                    );
                  })}
                </Accordion>
                <div className="w-full flex flex-col space-y-4 mt-2 items-end">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() =>
                      append({
                        description: "",
                        price: 0,
                        images: [],
                        sku: "",
                        on_hand: 0,
                        show_by_default: false,
                        attributes: {},
                      })
                    }
                    className="w-full border-dashed border-2 h-10 text-gray-600 hover:text-gray-800 hover:border-gray-400"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Add New Variant
                  </Button>
                </div>
              </div>

              <div className="grid grid-cols-1 gap-4 self-start sticky top-4 ">
                <FormField
                  control={form.control}
                  name="brand"
                  render={({ field }) => (
                    <FormItem className="w-full shadow-sm border border-gray-300 p-4 pl-5 rounded-md overflow-hidden">
                      <FormLabel className="text-base pb-2">Brand *</FormLabel>
                      <FormControl>
                        <ScrollArea className="h-64 border border-gray-300 rounded-md p-4 ">
                          <RadioGroup
                            onValueChange={field.onChange}
                            defaultValue={field.value}
                            className="flex flex-col"
                          >
                            {!brands || brands.length === 0 ? (
                              brandsTimeoutElapsed ? (
                                <div className="flex items-center justify-center py-4">
                                  <p className="text-gray-500">No brands found</p>
                                </div>
                              ) : (
                                <div className="flex items-center justify-center py-4">
                                  <LoaderCircle className="animate-spin w-6 h-6 text-primary" />
                                </div>
                              )
                            ) : (
                              brands.map((brand) => (
                                <FormItem
                                  key={brand?.id}
                                  className="flex items-center space-x-3 space-y-0 shadow-sm border border-gray-300 py-2 px-2 rounded-md"
                                >
                                  <FormControl>
                                    <RadioGroupItem value={brand?.id ?? " "} />
                                  </FormControl>
                                  <FormLabel className="font-normal">
                                    {capitalizeWords(brand?.name || "")}
                                  </FormLabel>
                                </FormItem>
                              ))
                            )}
                          </RadioGroup>
                        </ScrollArea>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="categories"
                  render={({ field }) => (
                    <FormItem className="w-full shadow-sm border border-gray-300 p-4 pl-5 rounded-md overflow-hidden">
                      <FormLabel className="text-base pb-2">Categories *</FormLabel>
                      <FormControl>
                        <ScrollArea className="h-64   border border-gray-300 rounded-md p-4 ">
                          {!categories || categories.length === 0 ? (
                            categoriesTimeoutElapsed ? (
                              <div className="flex items-center justify-center py-4">
                                <p className="text-gray-500">No categories found</p>
                              </div>
                            ) : (
                              <div className="flex items-center justify-center py-4">
                                <LoaderCircle className="animate-spin w-6 h-6 text-primary" />
                              </div>
                            )
                          ) : (
                            categories.map((cat) => (
                              <FormField
                                key={cat?.id}
                                control={form.control}
                                name="categories"
                                render={({ field }) => {
                                  return (
                                    <FormItem
                                      key={cat?.id}
                                      className="flex flex-row items-start space-x-3 space-y-0 mb-3  border border-gray-300 p-2 rounded-md "
                                    >
                                      <FormControl>
                                        <Checkbox
                                          checked={field.value?.includes(cat?.id ?? "")}
                                          onCheckedChange={(checked) => {
                                            return checked
                                              ? field.onChange([...field.value, cat.id])
                                              : field.onChange(
                                                  field.value?.filter((value) => value !== cat?.id)
                                                );
                                          }}
                                        />
                                      </FormControl>
                                      <FormLabel className="font-normal">
                                        {capitalizeWords(cat?.name || "")}
                                      </FormLabel>
                                    </FormItem>
                                  );
                                }}
                              />
                            ))
                          )}
                        </ScrollArea>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <Button
                className="w-full bg-black hover:bg-black/80"
                type="submit"
                data-submit-button="true"
              >
                {publishLoading ? (
                  <>
                    <LoaderCircle className="mr-1 h-4 w-4 animate-spin" />
                    Publishing...
                  </>
                ) : (
                  "Publish"
                )}
              </Button>
            </div>
          </form>
        </Form>
      </div>
    );
  }
);

export default AddProductForm;
